import React, { ElementType, useState } from 'react';

import { Flex, Box, VStack, IconProps, type ChakraProps, useOutsideClick } from '@chakra-ui/react';
import styled from '@emotion/styled';
import { L1_L2_NAV_WIDTH } from 'Deer/App';
import useOneOnOnePageParams from 'screens/oneOnOneRabbit/hooks/useOneOnOnePageParams';

import { DoubleArrowLeft } from 'assets/assetsV1/iconComponents';

import { CircularButton } from 'uiToolkitV1/CircularButton';
import { TextBodyLarge, TextLabel } from 'uiToolkitV1/Typography';

interface Props {
  menuItems: {
    label: string;
    Icon?: ElementType<IconProps>;
    onClick?: ({ instanceId }: { instanceId: number }) => void;
    RenderComponent: ElementType;
    HoverIcon?: ElementType<IconProps>;
  }[];
  sx?: ChakraProps['sx'];
  isExpanded?: boolean;
  setIsExpanded?: (isExpanded: boolean) => void;
  heading?: string;
  closeOnOutsideClick?: boolean;
  setSelectedOption?: (label: string) => void;
  wrapperRef?: React.RefObject<HTMLDivElement>;
}

interface NavButtonProps {
  label: string;
  Icon: ElementType<IconProps>;
  isActive?: boolean;
  onClick?: () => void;
  isExpanded?: boolean;
  HoverIcon?: ElementType<IconProps>;
}

const NavButton = ({ Icon, label, isActive, isExpanded, onClick, HoverIcon }: NavButtonProps) => {
  const [isHovered, setIsHovered] = useState(false);
  return (
    <Box
      sx={{
        height: '88px',
        width: '88px',
        background: isActive && isExpanded ? '#E6F1FD' : '#FDFDFD',
        borderRadius: '16px',
        alignItems: 'center',
        justifyContent: 'center',
        ':hover': {
          background: '#E6F1FD',
          svg: {
            color: '#104F95',
          },
        },
      }}
      display="flex"
      as="button"
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <Flex flexDir="column" alignItems="center">
        {!!Icon && !isHovered && !isActive && (
          <Icon boxSize="24px" fill="transparent" color={isActive ? '#104F95' : '#666666'} />
        )}
        {!!HoverIcon && (isHovered || isActive) && (
          <HoverIcon
            id="right-sidebar-hovered-icon"
            boxSize="24px"
            fill="transparent"
            color={isActive ? '#104F95' : '#666666'}
          />
        )}
        <TextLabel fontWeight={isActive ? 'bold' : 'normal'} color={isActive || isHovered ? '#104F95' : '#666666'}>
          {label}
        </TextLabel>
      </Flex>
    </Box>
  );
};

const CollapseButton = styled(CircularButton)`
  &.chakra-button {
    transition: left 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
    border-top-left-radius: 50px;
    border-bottom-left-radius: 50px;
    border-top-right-radius: 0px;
    border-bottom-right-radius: 0px;
    background-color: #ebeff3;
  }
`;

const NavigationSidebar = ({
  menuItems = [],
  sx,
  isExpanded,
  setIsExpanded,
  heading = '',
  closeOnOutsideClick = false,
  setSelectedOption,
  wrapperRef,
}: Props) => {
  const sidebarRef = React.useRef(null);

  const { instanceId } = useOneOnOnePageParams();

  const [activeIndex, setActiveIndex] = React.useState(-1);
  const RenderComp = menuItems[activeIndex]?.RenderComponent;

  // const onMouseEnter = () => {
  //   setIsExpanded(true);
  // };
  // const onMouseLeave = () => {
  //   setTimeout(() => {
  //     setIsExpanded(false);
  //   }, 300);
  // };

  useOutsideClick({
    ref: sidebarRef,
    handler: (e) => {
      // doing the ref check to make sure that the click is not on the button or a sidepanel
      // REFER: https://stackoverflow.com/questions/54391682/detect-click-outside-component-react-hooks/54392243#54392243
      if (closeOnOutsideClick && isExpanded && wrapperRef?.current?.contains(e?.target as Node)) {
        setIsExpanded(false);
        setActiveIndex(-1);
      }
    },
  });

  const onButtonClick = (index: number) => {
    setIsExpanded(true);
    setActiveIndex(index);
  };

  return (
    <Flex
      sx={{
        position: 'absolute',
        right: isExpanded ? '0px' : `-${L1_L2_NAV_WIDTH}`,
        transition: 'right 0.3s ease-in-out',
        ...sx,
      }}
      ref={sidebarRef}
    >
      <Flex
        className="nav-sidebar-container"
        // bg="#F5F7F9"
        flexDir="column"
        borderRadius="8px"
      >
        {isExpanded && (
          <Flex position="relative" bg="#FDFDFD" p="14px" borderRadius="8px 8px 0px 0px">
            {/* @ts-expect-error no children */}
            <CollapseButton
              onClick={() => {
                setIsExpanded(false);
                setActiveIndex(-1);
              }}
              sx={{
                position: 'absolute',
                top: '12px',
                left: '0px',
                transform: 'rotate(180deg)',
                'z-index': 50,
              }}
              variant="tertiary"
              StartIcon={DoubleArrowLeft}
              data-testid="nav-sidebar-collapse-button"
            />
            <TextBodyLarge ml="30px" bold>
              {heading}
            </TextBodyLarge>
          </Flex>
        )}
        <Flex overflow={isExpanded ? 'hidden' : 'inherit'} borderRadius="8px">
          <Box className="nav-sidebar-buttons">
            <VStack
              background="#FDFDFD"
              p="3px"
              spacing="0px"
              align="stretch"
              borderRadius="8px"
              gap="4px"
              data-testid="nav-sidebar-buttons-container"
            >
              {menuItems?.map((item, index) => (
                <NavButton
                  key={item.label}
                  label={item.label}
                  Icon={item.Icon}
                  HoverIcon={item.HoverIcon ?? item.Icon}
                  isActive={index === activeIndex}
                  onClick={() => {
                    onButtonClick(index);
                    if (typeof item.onClick === 'function') {
                      item.onClick({
                        instanceId,
                      });
                    }
                    if (typeof setSelectedOption === 'function') {
                      setSelectedOption(item?.label);
                    }
                  }}
                  isExpanded={isExpanded}
                />
              ))}
            </VStack>
          </Box>

          <Box
            className="nav-sidebar-render-comp-container"
            flex="1"
            bg="#F5F7F9"
            p="4px"
            maxW={L1_L2_NAV_WIDTH}
            w={L1_L2_NAV_WIDTH}
            overflow="auto"
            data-testid="instance-sidebar-container"
          >
            {!!RenderComp && <RenderComp />}
          </Box>
        </Flex>
      </Flex>
    </Flex>
  );
};

export { NavigationSidebar };
